// CL-3 injectPlaceholderForEmpty (omo F-10 영감).
//
// content가 비어있고 tool_calls도 없는 assistant message를 탐지 → placeholder 주입.
// 다음 turn에서 의미 있는 응답 유도. OL-3 `<final/>` marker와 보완 (B2 empty-stop family 80%+).
//
// tool_calls 있는 assistant는 건드리지 않음 (CL-2 repairOrphanedToolCalls 영역).
//
// 의존성: CL-1 errorClassifier ERROR_TYPES + RECOVERY_TABLE registerRecoveryHandler
//
// 사용 시점:
//   1. preflight — 매 OpenAI 호출 직전 1회 호출 (pure repair, 새 배열 반환)
//   2. recovery handler — CL-1 dispatchRecovery(ERROR_TYPES.EMPTY_MESSAGE, ctx) 분기
//      (registerEmptyMessageRecoveryHandler 명시 호출로 등록, 자동 register X)

import { ERROR_TYPES } from './errorClassifier.js';
import { registerRecoveryHandler } from './RECOVERY_TABLE.js';

const EMPTY_PLACEHOLDER_CONTENT = '(Retry: previous response was empty — please continue.)';

function _isEmptyContent(content) {
  if (content === null || content === undefined) return true;
  if (typeof content === 'string') return content.trim().length === 0;
  // array content (OpenAI multi-modal) — 모든 part가 empty면 true. 보수적으로 비어있으면 true.
  if (Array.isArray(content)) return content.length === 0;
  return false;
}

function _hasToolCalls(msg) {
  return Array.isArray(msg.tool_calls) && msg.tool_calls.length > 0;
}

/**
 * messages[] 스캔 → empty assistant message에 placeholder content 주입.
 * 원본 mutation 0 — 새 배열 + 새 객체 반환.
 *
 * @param {Array<object>} messages
 * @returns {{messages: Array<object>, injectedCount: number, injectedIndexes: number[]}}
 */
export function injectPlaceholderForEmpty(messages) {
  if (!Array.isArray(messages)) {
    return { messages, injectedCount: 0, injectedIndexes: [] };
  }

  const out = [];
  const injectedIndexes = [];
  let injectedCount = 0;

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];
    if (msg && msg.role === 'assistant' && !_hasToolCalls(msg) && _isEmptyContent(msg.content)) {
      // 새 객체로 교체 — 원본 mutation 0
      out.push({ ...msg, content: EMPTY_PLACEHOLDER_CONTENT });
      injectedIndexes.push(i);
      injectedCount++;
    } else {
      out.push(msg);
    }
  }

  return { messages: out, injectedCount, injectedIndexes };
}

/**
 * CL-1 RECOVERY_TABLE에 empty_message handler 등록.
 *
 * caller(boot path)가 명시 호출. import만으로는 register 안 됨 (부수효과 0).
 *
 * handler 동작:
 *   - ctx.messages array를 in-place mutation (caller 참조 보존)
 *   - injectedCount ≥ 1 → true (recovery 성공)
 *   - 0 → false (recover 불가)
 *   - ctx 또는 ctx.messages 누락/non-array → false
 */
export function registerEmptyMessageRecoveryHandler() {
  return registerRecoveryHandler(ERROR_TYPES.EMPTY_MESSAGE, async (ctx) => {
    if (!ctx || !Array.isArray(ctx.messages)) return false;
    const result = injectPlaceholderForEmpty(ctx.messages);
    if (result.injectedCount === 0) return false;
    ctx.messages.length = 0;
    for (const m of result.messages) ctx.messages.push(m);
    return true;
  });
}

export const EMPTY_PLACEHOLDER_CONTENT_FOR_TEST = EMPTY_PLACEHOLDER_CONTENT;
export const _isEmptyContentForTest = _isEmptyContent;
export const _hasToolCallsForTest = _hasToolCalls;
